import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography, Stack, IconButton, FormControl, InputLabel, Select, MenuItem, Checkbox, Alert, Button } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { updateTabSetting } from '../../../actions/apiDealerUpdates';
import HelpTooltip from '../../../components/HelpTooltip';

const FormList = ({ custom_forms, handleEdit, handleDelete, openNewFormModal }) => {
  const dispatch = useDispatch();
  const all_tabs = useSelector(state => state.settings_site?.all_tabs || []);
  const [error, setError] = useState(null);

  const handleTabChange = async (form, selectedTabIds) => {
    try {
      // Update each selected tab to set tab.form = form.name
      const updatePromises = selectedTabIds.map(tabId => {
        const data = { form: form.name };
        return dispatch(updateTabSetting(tabId, data, 'editTabPropertyError'));
      });
      
      // Clear form field for tabs that were previously assigned to this form but are no longer selected
      const previouslyAssignedTabs = all_tabs.filter(tab => tab.form === form.name && !selectedTabIds.includes(tab.id));
      const clearPromises = previouslyAssignedTabs.map(tab => {
        return dispatch(updateTabSetting(tab.id, { form: '' }, 'editTabPropertyError'));
      });

      await Promise.all([...updatePromises, ...clearPromises]);
      setError(null);
    } catch (ex) {
      setError('Failed to update tab assignments. Please try again.');
      console.error('Tab update error:', ex);
    }
  };

  return (
    <Box sx={{ width: 380, borderRight: '1px solid #e0e0e0', pr: 2, overflowY: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Forms</Typography>
        <Stack direction="row" alignItems="center">
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openNewFormModal}>
            New
          </Button>
          <HelpTooltip title="Create a new inspection form." />
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
        {custom_forms.map(form => {
          const assignedTabs = all_tabs
            .filter(tab => tab.form === form.name)
            .map(tab => tab.id);

          return (
            <Paper
              key={form.id}
              sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
              onClick={() => handleEdit(form)}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1">{form.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.custom_form_properties?.length || 0} properties
                  </Typography>
                </Box>
                <Box onClick={e => e.stopPropagation()}>
                  <Stack direction="row" alignItems="center">
                    <IconButton size="small" onClick={() => handleEdit(form)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(form.id)}>
                      <Delete />
                    </IconButton>
                    <HelpTooltip title="Edit or delete this form." />
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tabs</InputLabel>
                  <Select
                    multiple
                    value={assignedTabs}
                    onChange={(e) => handleTabChange(form, e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.length === 0 ? (
                          <Box sx={{ color: 'text.secondary' }}>None</Box>
                        ) : (
                          selected.map(tabId => {
                            const tab = all_tabs.find(t => t.id === tabId);
                            return (
                              <Box key={tabId} sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}>
                                {tab?.name || tabId}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    )}
                  >
                    <MenuItem value="">
                      <Checkbox checked={assignedTabs.length === 0} />
                      <em>None</em>
                    </MenuItem>
                    {all_tabs.map(tab => (
                      <MenuItem key={tab.id} value={tab.id}>
                        <Checkbox checked={assignedTabs.includes(tab.id)} />
                        {tab.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpTooltip title="Assign this form to one or more tabs." />
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default FormList;